import { useMemo } from "react";
import { InputBase, ListSubheader, MenuItem, OutlinedInput, Select } from "@mui/material";
import { hasSubclass } from "../../../constants/modelSubclass";
import { McGroup, NO_MODEL_LABEL, mcLabel } from "./vtModel";
import { FAINT, MENU_BG, cellSx, fieldSx, menuItemSx, menuPaperSx } from "./vtTheme";

const PICK = "__pick__";

interface ModelClassSelectProps {
  value: string;
  groups: McGroup[];
  onChange: (value: string) => void;
  /** "cell": borderless in a table row; "field": an outlined form field. */
  variant: "cell" | "field";
  mod?: boolean;
  struck?: boolean;
  disabled?: boolean;
  /** Shown instead of a value (the bulk bar's "Set model class…"). */
  placeholder?: string;
  id?: string;
  ariaLabel?: string;
  height?: number;
  onFocus?: () => void;
}

/** One menu for the model class and, where the model needs one, its fuel
 *  model: Scott & Burgan and FBP codes are grouped under their class. */
export default function ModelClassSelect({
  value, groups, onChange, variant, mod, struck, disabled, placeholder, id, ariaLabel, height, onFocus,
}: ModelClassSelectProps) {
  const known = useMemo(() => new Set(["", ...groups.flatMap((g) => g.options.map((o) => o.value))]), [groups]);
  const items = useMemo(() => {
    const out = [
      <MenuItem key="" value="" sx={menuItemSx}>{NO_MODEL_LABEL} (no fire model)</MenuItem>,
    ];
    groups.forEach((g) => {
      if (!g.options.length) return;
      out.push(
        <ListSubheader key={`h:${g.label}`} sx={{ bgcolor: MENU_BG, color: FAINT, fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", lineHeight: "30px" }}>
          {g.label}
        </ListSubheader>,
      );
      g.options.forEach((o) => out.push(<MenuItem key={o.value} value={o.value} sx={{ ...menuItemSx, pl: 3 }}>{o.label}</MenuItem>));
    });
    return out;
  }, [groups]);

  const current = placeholder ? PICK : value;
  const extra = !placeholder && !known.has(value)
    ? [<MenuItem key={value} value={value} sx={menuItemSx}>{mcLabel(value)}</MenuItem>]
    : [];
  const pick = placeholder
    ? [<MenuItem key={PICK} value={PICK} disabled sx={{ ...menuItemSx, display: "none" }}>{placeholder}</MenuItem>]
    : [];

  return (
    <Select
      id={id}
      value={current}
      displayEmpty
      disabled={disabled}
      onChange={(e) => { if (e.target.value !== PICK) onChange(String(e.target.value)); }}
      onFocus={onFocus}
      renderValue={(v) => (v === PICK ? placeholder : mcLabel(String(v)))}
      input={variant === "cell" ? <InputBase /> : <OutlinedInput />}
      inputProps={{ "aria-label": ariaLabel, title: placeholder ? undefined : mcLabel(value) }}
      MenuProps={{ PaperProps: { sx: menuPaperSx }, MenuListProps: { dense: true } }}
      sx={variant === "cell"
        ? cellSx({ mod, struck })
        : { ...fieldSx({ mod }), width: "100%", ...(height ? { height, ".MuiInputBase-input": { py: 0 } } : {}), ...(placeholder ? { color: "rgba(255,255,255,0.7)" } : {}) }}
    >
      {[...pick, ...items, ...extra]}
    </Select>
  );
}

interface McFilterSelectProps {
  value: string;
  classes: string[];
  onChange: (value: string) => void;
  ariaLabel: string;
  width?: number;
}

/** Filter by model class: "*" all, "" no fire model, or a class (any fuel model). */
export function McFilterSelect({ value, classes, onChange, ariaLabel, width = 220 }: McFilterSelectProps) {
  return (
    <Select
      size="small"
      value={value}
      displayEmpty
      onChange={(e) => onChange(String(e.target.value))}
      inputProps={{ "aria-label": ariaLabel }}
      MenuProps={{ PaperProps: { sx: menuPaperSx }, MenuListProps: { dense: true } }}
      sx={{ ...fieldSx(), width, height: 36 }}
    >
      <MenuItem value="*" sx={menuItemSx}>All model classes</MenuItem>
      <MenuItem value="" sx={menuItemSx}>{NO_MODEL_LABEL}</MenuItem>
      {classes.map((c) => (
        <MenuItem key={c} value={c} sx={menuItemSx}>{hasSubclass(c) ? `${c} (any)` : c}</MenuItem>
      ))}
    </Select>
  );
}
